import React, {useEffect, useState} from 'react';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import {Box, Grid, Typography} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import './AddCodingStamp.scss';
import {t} from 'i18next';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  useGetCodingStamp,
  useSaveUserCodingStamp,
} from '../../../../../hooks/document/useDocument';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as TagTreeIcon} from '../../../../../assets/images/tag-tree.svg';
import {ToastType} from '../../../../../shared/constants';
import {useSelector} from 'react-redux';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {CodingFormItem} from '../../../../../shared/types';
import {RootState} from '../../../../../redux/store';

interface AddCodingProps {
  showDrawer?: boolean;
  handleDrawer?: (value: React.SetStateAction<boolean>) => void;
  codingForm?: CodingFormItem[];
  codingFormId?: number | string | undefined;
  onSuccess?: (object: any) => void;
  panelId?: string;
}
const AddCodingStamp = ({
  showDrawer,
  handleDrawer,
  codingForm,
  codingFormId,
  onSuccess,
  panelId,
}: AddCodingProps) => {
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [codingStampName, setCodingStampName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);

  const [touchedFields, setTouchedFields] = useState<{
    codingStampName: boolean;
  }>({
    codingStampName: false,
  });
  const docView = useSelector((state: any) => state.docView);
  const {mutate: mutateGetCodingStamp, data: codingStampData} =
    useGetCodingStamp();
  const {mutate: mutateSaveUserCodingStamp} = useSaveUserCodingStamp();
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const activeDocumentId = Number(activeDocumentObj?.documentId);

  useEffect(() => {
    mutateGetCodingStamp({
      sourceID: selectedSourceId ?? 0,
      userCodingFormID: Number(codingFormId),
    });
  }, []);
  const handleBlur = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value, name} = event.target;

    if (name === 'codingStampName') {
      setCodingStampName(value);
    }
  };

  const handleSelectChange = (value: any) => {
    const newValue = {...value, stamp: JSON.parse(value.stamp)};
    setSelectedStamp(newValue);
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
    setTimeout(() => {
      if (status === ToastType.Success) {
        setShowToast(false);
        onSuccess(selectedStamp);
        handleDrawer(false);
      }
    }, 1000);
  };

  const selectedSourceId = useSelector(
    (state: RootState) => state.docView.sourceId
  );

  const handleSaveCodingStamp = () => {
    const payload = {
      docViewSaveData: {
        codedObject: codingForm?.map((item) => ({
          id: item?.value.toString(),
          type: item.groupAssociation ? item.groupAssociation.value : item.type,
          value:
            item.type === 'COMMENT'
              ? (item?.controlValue ?? '')
              : (item?.controlValue ?? 'False'),
          fieldName: item?.friendlyLabel || '',
          controlType: item?.displayType || '',
        })),
        isComplete: 1,
        currentDocID: activeDocumentId,
        sourceTypeCode: docView?.sourceTypeCode || 'DOCEXPLORER',
        sourceID: 0,
        stampColorCode: '',
        codingFormID: Number(codingFormId),
        ingestionGuid: '',
        documentGuid: '',
      },
      sessionObject: {
        documentListMasterID: 68,
        sourceTypeCode: docView?.sourceTypeCode || 'DOCEXPLORER',
        docsForCodeSame: [''],
        assignmentSource: 'REVIEWERDASHBOARD',
        sourceID: selectedSourceId ?? 0,
        userCodingFormID: Number(codingFormId),
        xmlCompletedDocumentEditedCodingForm: '',
      },
      colorCode: selectedStamp?.label,
      stampName: codingStampName,
      codingFormStampId: selectedStamp?.stamp?.codingFormUserStampID,
      isDeleted: 0,
    };

    mutateSaveUserCodingStamp(payload, {
      onSuccess: (data) => {
        triggerToast(
          data.message ||
            t('docView.headers.codingForm.codingStampSavedSucces'),
          ToastType.Success
        );
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
        setError(error.message);
      },
    });
  };
  const contentBody = (
    <Box
      className="add-coding-stamp-container"
      data-tab-group={panelId}
      data-tab-id="add-coding-stamp-container"
    >
      <Box className="add-keywords-header-box">
        <Box className="title-box">
          <Typography
            className="typo-text headline3_size title"
            data-testid="add-coding-stamp-title"
          >
            {t('docView.headers.codingForm.addNewCodingStamp')}&nbsp;
          </Typography>
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              handleDrawer(false);
            }}
            disabled={false}
            dataTestId="add-coding-stamp-cancel-button"
            data-tab-group="add-coding-stamp"
            data-tab-id="add-coding-stamp-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            onClick={handleSaveCodingStamp}
            disabled={!codingStampName || !selectedStamp}
            dataTestId="add-coding-stamp-save-btn"
            data-tab-group="add-coding-stamp"
            data-tab-id="add-coding-stamp-save-button"
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box
        className={`content-box`}
        data-tab-group="add-coding-stamp"
        data-tab-id="add-code-container"
      >
        <Grid
          container
          className="content-box-grid-container"
          data-tab-group={panelId}
          data-tab-id="content-box-grid-container"
        >
          <Grid size={{xs: 12, sm: 6}}>
            <ArrSingleSelectDropdown
              tabIndex={0}
              menuPropClassName="add-coding-stamp-menu"
              dropdownLabel={t('docView.headers.codingForm.selectStamp')}
              dropdownItems={codingStampData?.map((stamp, index) => ({
                key: index,
                value: stamp.isDisabled ? 'Assigned' : 'New Stamp',
                label: stamp.codingFormStampColorLabel,
                id: stamp.codingFormStampColorRGB,
                name: stamp.codingFormStampColorCode,
                disabled: stamp.isDisabled,
                stamp: JSON.stringify(stamp),
              }))}
              selectedValues={selectedStamp}
              dropdownItemKey={'key'}
              dropdownItemDisplay={'value'}
              formControlRestProps={{}}
              onSelectionChange={handleSelectChange}
              dataTestId="select-stamp-dropdown"
              renderPrefix={(item: any) => (
                <ArrIconButton
                  tabIndex={0}
                  key={item.CodingFormStampColorLabel}
                  shape="none"
                  size="small"
                  icon={<TagTreeIcon style={{color: `#${item.id}`}} />}
                  ariaLabel="edit-stamp"
                  tooltipContent={item?.CodingFormStampColorName}
                  testId={`edit-stamp-${item.codingFormStampColorLabel}`}
                />
              )}
              renderSelectedPrefix={(item: any) => (
                <ArrIconButton
                  tabIndex={0}
                  key={item?.CodingFormStampColorLabel}
                  shape="none"
                  size="small"
                  icon={<TagTreeIcon style={{color: `#${item?.id}`}} />}
                  ariaLabel="edit-stamp"
                  tooltipContent={item?.CodingFormStampColorName}
                  testId="edit-stamp-selected"
                />
              )}
              data-tab-group={panelId}
              data-tab-id="code-select-dropdown"
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <ArrTextField
              id="coding-stamp-name-input"
              state={
                touchedFields.codingStampName && !codingStampName
                  ? 'error'
                  : 'enabled'
              }
              label={t('docView.headers.codingForm.codingStampName')}
              name="codingStampName"
              aria-label={t('docView.headers.codingForm.codingStampName')}
              value={codingStampName}
              onChange={handleTextInputChange}
              onBlur={() => handleBlur('codingStampName')}
              dataTestId="coding-stamp-first-name"
              supportText={''}
              showErrorIcon={error ?? false}
              aria-invalid={error ?? false}
              tabIndex={0}
            />
          </Grid>
        </Grid>
      </Box>

      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showHeader={false}
          showButton={false}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
        >
          {contentBody}
        </ArrDrawer>
      )}
    </>
  );
};

export default AddCodingStamp;
